import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { ReactNode } from "react";

import { useReviewsContext } from "@/components/Product/Reviews/context/reviews.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as UseFeatureTracker from "@/services/tracking/trackers/feature/useFeature.tracker";

type StepComponentProps = {
    step: number;
    title: string;
    complete: boolean;
    skipped: boolean;
    active: boolean;
    skippable: boolean;
    children: ReactNode;
    skip: () => void;
    edit: () => void;
};

export const StepComponent = ({
    step,
    title,
    complete,
    skipped,
    active,
    skippable,
    children,
    skip,
    edit
}: StepComponentProps) => {
    const { t } = useGranite();

    const { sku, brand, category } = useReviewsContext();

    const handleOnSubmitClick = () => {
        UseFeatureTracker.track({
            type: "bazaarvoice",
            name: "submit",
            sku: sku,
            brand: brand,
            categoryId: category
        });
    };

    return (
        <div className={cn("tw:border-t tw:border-t-black tw:py-4")}>
            <p className={cn("tw:flex tw:justify-between tw:m-0")}>
                <span className={cn("tw:flex tw:items-center")}>
                    <span
                        className={cn(
                            "tw:h-6 tw:w-6 tw:border tw:border-black tw:rounded-full tw:leading-6 tw:flex tw:justify-center"
                        )}
                    >
                        {step}
                    </span>
                    <span className={cn("tw:ml-2")}>{title}</span>
                </span>
                <span className={cn("tw:flex")}>
                    <span
                        className={cn("tw:rounded-sm tw:px-2", {
                            "tw:text-white tw:bg-success": complete && !active,
                            "tw:bg-grey-light": skipped && !active,
                            "tw:text-white tw:bg-primary": active
                        })}
                    >
                        {complete &&
                            !active &&
                            t("product.reviews.writeReview.step.complete")}
                        {skipped &&
                            !active &&
                            t("product.reviews.writeReview.step.skipped")}
                        {active && t("product.reviews.writeReview.step.active")}
                    </span>
                    {(complete || skipped) && !active && (
                        <Button
                            onClick={edit}
                            outline={true}
                            className={cn("tw:py-0 tw:px-2 tw:ml-2")}
                        >
                            {t("product.reviews.writeReview.step.edit")}
                        </Button>
                    )}
                </span>
            </p>
            {active && (
                <div className={cn("tw:mt-4")}>
                    {children}
                    <div className={cn("tw:mt-4")}>
                        <Button
                            type={"submit"}
                            onClickCapture={handleOnSubmitClick}
                        >
                            {step === 1 &&
                                t("product.reviews.writeReview.step.send")}
                            {step !== 1 &&
                                t("product.reviews.writeReview.step.submit")}
                        </Button>
                        {skippable && (
                            <Button
                                className={cn("tw:ml-2")}
                                color={"gray"}
                                onClick={skip}
                            >
                                {t("product.reviews.writeReview.step.skip")}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
